// The hook that owns the SSE reader: fetch + getReader() + TextDecoder + frame parser
// + AbortController. One investigation at a time. State machine (REF-05 s.2-s.3):
//   idle → running → (completed | refused | failed)
import { useCallback, useEffect, useRef, useState } from 'react';
import { tokenManager } from '../../utils/tokenManager';
import { postEventStream } from './sseClient';
import type {
  InvestigationInput,
  InvestigationReport,
  RunStatus,
  StepEvent,
  StreamError,
} from './types';

const INVESTIGATE_PATH = '/api/backend/ai-troubleshooting/investigate';

export interface UseInvestigationStreamResult {
  status: RunStatus;
  steps: StepEvent[];
  report: InvestigationReport | null;
  error: StreamError | null;
  investigationId: string | null;
  run: (input: InvestigationInput) => Promise<void>;
  abort: () => void;
}

export function useInvestigationStream(
  onComplete?: (report: InvestigationReport) => void,
): UseInvestigationStreamResult {
  const [status, setStatus] = useState<RunStatus>('idle');
  const [steps, setSteps] = useState<StepEvent[]>([]);
  const [report, setReport] = useState<InvestigationReport | null>(null);
  const [error, setError] = useState<StreamError | null>(null);
  const [investigationId, setInvestigationId] = useState<string | null>(null);

  const controllerRef = useRef<AbortController | null>(null);
  // Tracks whether a terminal (report | error) event arrived for the current run so
  // `done` can detect a stream that ended without a terminal event.
  const terminalSeenRef = useRef(false);

  const abort = useCallback(() => {
    controllerRef.current?.abort();
  }, []);

  const run = useCallback(
    async (input: InvestigationInput) => {
      // Cancel any prior in-flight run first.
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      terminalSeenRef.current = false;

      setStatus('running');
      setSteps([]);
      setReport(null);
      setError(null);
      setInvestigationId(null);

      let token: string | null = null;
      try {
        token = await tokenManager.getAccessToken(); // async, refresh-aware; may be null
      } catch {
        if (controller.signal.aborted) return;
        setError({
          code: 'AI_SESSION_EXPIRED',
          message: 'Your session has expired. Please sign in again.',
        });
        setStatus('failed');
        return;
      }

      if (controller.signal.aborted) return;

      await postEventStream(INVESTIGATE_PATH, input, {
        token,
        signal: controller.signal,
        onEvent: (e) => {
          // Ignore late events from a superseded/aborted run.
          if (controller.signal.aborted) return;
          switch (e.event) {
            case 'created':
              setInvestigationId(e.data?.investigationId ?? null);
              break;
            case 'step':
              setSteps((prev) => [...prev, e.data as StepEvent]);
              break;
            case 'report': {
              terminalSeenRef.current = true;
              const rep = e.data as InvestigationReport;
              setReport(rep);
              // report.status is 'completed' | 'refused'.
              setStatus(rep.status);
              onComplete?.(rep);
              break;
            }
            case 'error':
              terminalSeenRef.current = true;
              setError(e.data as StreamError);
              setStatus('failed');
              break;
            case 'done':
              if (!terminalSeenRef.current) {
                setError({
                  code: 'AI_STREAM_INCOMPLETE',
                  message: 'The stream ended unexpectedly before a result was produced.',
                });
                setStatus('failed');
              }
              break;
            default:
              break;
          }
        },
      }).catch((err: unknown) => {
        // Unmount / new-run abort: swallow.
        if (controller.signal.aborted) return;
        const message =
          err instanceof Error ? err.message : typeof err === 'string' ? err : String(err);
        setError({ code: 'AI_STREAM_NETWORK', message });
        setStatus('failed');
      });
    },
    [onComplete],
  );

  // Abort any in-flight stream on unmount.
  useEffect(
    () => () => {
      controllerRef.current?.abort();
    },
    [],
  );

  return { status, steps, report, error, investigationId, run, abort };
}
